import React, { useRef, useEffect } from 'react'
import { CHECKPOINT_RADIUS } from '../utils/tracing'
import './ShapeCanvas.css'

const CANVAS_SIZE = 280
const LOGICAL_SIZE = 200
const PADDING = (CANVAS_SIZE - LOGICAL_SIZE) / 2

function toCanvas(point) {
  return { x: point.x + PADDING, y: point.y + PADDING }
}

function ShapeCanvas({
  side,
  shapePath,
  clearedCount,
  userPath,
  isActive,
  bothActive,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}) {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    if (!shapePath || shapePath.length === 0) return

    // Guide path connecting checkpoints.
    ctx.strokeStyle = '#cfd8dc'
    ctx.lineWidth = 6
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    const first = toCanvas(shapePath[0])
    ctx.moveTo(first.x, first.y)
    for (let i = 1; i < shapePath.length; i++) {
      const p = toCanvas(shapePath[i])
      ctx.lineTo(p.x, p.y)
    }
    ctx.stroke()

    // Checkpoints. The "next" uncleared checkpoint gets a slightly larger
    // radius so it reads as the active target wherever the kid currently is.
    shapePath.forEach((point, idx) => {
      const p = toCanvas(point)
      const cleared = idx < clearedCount
      const isNext = idx === clearedCount
      const radius = isNext ? CHECKPOINT_RADIUS * 0.6 : CHECKPOINT_RADIUS / 2
      ctx.beginPath()
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2)
      if (cleared) {
        ctx.fillStyle = '#4caf50'
      } else if (isNext) {
        ctx.fillStyle = '#2196f3'
      } else {
        ctx.fillStyle = '#9e9e9e'
      }
      ctx.fill()
    })

    // Direction-indicator triangle on the NEXT uncleared checkpoint, pointing
    // toward the one after it. Sits inside the (larger, blue) "next" dot so
    // the kid always sees a clear "go this way" arrow at the active spot —
    // not stuck on the start dot after it has been cleared.
    if (clearedCount < shapePath.length - 1) {
      const next = toCanvas(shapePath[clearedCount])
      const after = toCanvas(shapePath[clearedCount + 1])
      const angle = Math.atan2(after.y - next.y, after.x - next.x)
      const size = 6
      ctx.save()
      ctx.translate(next.x, next.y)
      ctx.rotate(angle)
      ctx.beginPath()
      ctx.moveTo(size, 0)
      ctx.lineTo(-size * 0.6, size * 0.7)
      ctx.lineTo(-size * 0.6, -size * 0.7)
      ctx.closePath()
      ctx.fillStyle = '#ffffff'
      ctx.fill()
      ctx.restore()
    }

    // User trace.
    if (userPath && userPath.length > 1) {
      ctx.strokeStyle = bothActive ? '#4caf50' : '#ff9800'
      ctx.lineWidth = 4
      ctx.beginPath()
      const start = toCanvas(userPath[0])
      ctx.moveTo(start.x, start.y)
      for (let i = 1; i < userPath.length; i++) {
        const p = toCanvas(userPath[i])
        ctx.lineTo(p.x, p.y)
      }
      ctx.stroke()
    }
  }, [shapePath, clearedCount, userPath, bothActive])

  const getLogicalCoords = (clientX, clientY) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    const canvasX = ((clientX - rect.left) / rect.width) * CANVAS_SIZE
    const canvasY = ((clientY - rect.top) / rect.height) * CANVAS_SIZE
    return { x: canvasX - PADDING, y: canvasY - PADDING }
  }

  const fireDown = (clientX, clientY) => {
    onPointerDown?.(side, getLogicalCoords(clientX, clientY))
  }
  const fireMove = (clientX, clientY) => {
    onPointerMove?.(side, getLogicalCoords(clientX, clientY))
  }
  const fireUp = () => {
    onPointerUp?.(side)
  }

  // Touch handlers — registered natively with { passive: false } so
  // e.preventDefault() actually works in iOS Safari. React's synthetic
  // touch events are registered as passive in newer React, which silently
  // turns preventDefault into a no-op and lets iOS hijack the gesture for
  // pinch-zoom / scroll. Routing two fingers to the right canvas (one each)
  // also relies on this — see the changedTouches[0] note below.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const handleTouchStart = (e) => {
      e.preventDefault()
      // Multi-touch: e.touches lists every active touch on the page, so
      // touches[0] may belong to the OTHER hand's canvas. changedTouches
      // is the touch that just started on THIS canvas.
      const touch = e.changedTouches[0]
      if (touch) fireDown(touch.clientX, touch.clientY)
    }

    const handleTouchMove = (e) => {
      e.preventDefault()
      const touch = e.changedTouches[0]
      if (touch) fireMove(touch.clientX, touch.clientY)
    }

    const handleTouchEnd = (e) => {
      e.preventDefault()
      fireUp()
    }

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false })
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false })
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false })

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart)
      canvas.removeEventListener('touchmove', handleTouchMove)
      canvas.removeEventListener('touchend', handleTouchEnd)
      canvas.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [side, onPointerDown, onPointerMove, onPointerUp])

  return (
    <div className={`shape-canvas shape-canvas--${side}`}>
      <canvas
        ref={canvasRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        className={`shape-canvas__surface${isActive ? ' is-active' : ''}`}
        data-testid={`shape-canvas-${side}`}
        onMouseDown={(e) => fireDown(e.clientX, e.clientY)}
        onMouseMove={(e) => {
          if (isActive) fireMove(e.clientX, e.clientY)
        }}
        onMouseUp={fireUp}
        onMouseLeave={() => {
          if (isActive) fireUp()
        }}
      />
    </div>
  )
}

export default ShapeCanvas
export { CANVAS_SIZE, PADDING }
